import { useNavigate } from 'react-router-dom';
import { Search, BarChart2, BookMarked, Check, ArrowRight, Stethoscope, Building2, Users, UserCheck } from 'lucide-react';
import { DPC_PRICE_DISPLAY } from '../config/pricing';

export default function DPCLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#F0EEE9] min-h-screen">

      {/* Hero */}
      <section className="bg-[#0D3D4A] text-white py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs tracking-widest uppercase font-semibold text-white/60 mb-4">
            Meridian for Physicians
          </p>
          <h1
            className="text-5xl md:text-6xl font-medium mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Help your patients find<br />the best cash prices.
          </h1>
          <p className="text-lg md:text-xl text-white/75 mb-10 max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Search 600,000+ hospital price records across 2,000+ facilities nationwide —
            and get your patients to the right facility, at the right price, in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/dpc/register')}
              className="bg-white text-[#0D3D4A] px-8 py-4 font-semibold text-sm hover:bg-[#F0EEE9] transition-colors"
              data-testid="hero-start-trial-btn"
            >
              Start Free 14-Day Trial
            </button>
            <button
              onClick={() => navigate('/dpc/login')}
              className="border border-white/40 text-white px-8 py-4 font-semibold text-sm hover:border-white/80 transition-colors"
              data-testid="hero-signin-btn"
            >
              Sign In
            </button>
          </div>
          <p className="text-white/40 text-xs mt-5">No credit card required during trial</p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-3xl font-medium text-stone-900 text-center mb-14"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Built for the physician workflow
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                title: 'Instant Price Search',
                desc: 'Search 600,000+ hospital price records across 2,000+ facilities nationwide. Find imaging, surgical, and diagnostic prices in any state — in seconds.',
              },
              {
                icon: BarChart2,
                title: 'Compare & Refer',
                desc: 'Compare facilities side-by-side on price, location, and quality indicators. Generate a clear referral summary to hand to your patient.',
              },
              {
                icon: BookMarked,
                title: 'Save & Track',
                desc: 'Build a library of common searches for your patient population. Save your go-to facilities and procedures — no re-searching every visit.',
              },
            ].map((f, i) => (
              <div key={i} className="p-8 border border-stone-200 hover:border-[#0D3D4A] transition-colors">
                <div className="w-11 h-11 bg-[#0D3D4A]/10 flex items-center justify-center mb-5">
                  <f.icon size={22} className="text-[#0D3D4A]" />
                </div>
                <h3
                  className="text-lg font-semibold text-stone-900 mb-3"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {f.title}
                </h3>
                <p className="text-stone-500 text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 px-6 md:px-12 bg-[#F0EEE9]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Who It's For</p>
              <h2
                className="text-3xl font-medium text-stone-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                For physicians who put patients first.
              </h2>
              <p className="text-stone-500 text-sm leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Whether you're running a Direct Primary Care practice, a concierge medicine clinic, or simply
                referring patients who pay cash — Meridian gives you the data to make confident referrals.
              </p>
              <button
                onClick={() => navigate('/dpc/register')}
                className="flex items-center gap-2 bg-[#0D3D4A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors"
              >
                Get Started Free <ArrowRight size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Stethoscope, label: 'Direct Primary Care (DPC)' },
                { icon: UserCheck,   label: 'Concierge Medicine' },
                { icon: Building2,   label: 'Independent Physicians' },
                { icon: Users,       label: 'Cash-Pay Referrals' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-stone-200 p-5">
                  <item.icon size={20} className="text-[#0D3D4A] mb-3" />
                  <p className="text-sm font-medium text-stone-800" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Pricing</p>
          <h2
            className="text-3xl font-medium text-stone-900 mb-10"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Simple, transparent pricing.
          </h2>

          <div className="border-2 border-[#0D3D4A] p-10">
            <p className="text-sm font-semibold text-[#0D3D4A] uppercase tracking-wider mb-2">Physician Plan</p>
            <div className="flex items-end justify-center gap-1 mb-2">
              <span className="text-5xl font-bold text-stone-900">{DPC_PRICE_DISPLAY}</span>
              <span className="text-stone-500 pb-1">/month</span>
            </div>
            <p className="text-sm text-stone-400 mb-8">14-day free trial — no credit card required</p>

            <ul className="text-left space-y-3 mb-10">
              {[
                'Unlimited procedure price searches',
                'Imaging price search (CT, MRI, X-ray, Ultrasound)',
                'Side-by-side facility comparison',
                'Patient referral summaries',
                'Saved search library',
                'Priority support',
                '14-day free trial — no credit card required',
                'Cancel anytime',
              ].map((feat, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-stone-700">
                  <Check size={15} className="text-[#0D3D4A] flex-shrink-0" />
                  {feat}
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate('/dpc/register')}
              className="w-full bg-[#0D3D4A] text-white py-4 font-semibold text-sm hover:bg-[#0E4557] transition-colors"
              data-testid="pricing-start-trial-btn"
            >
              Start Free Trial
            </button>
            <p className="text-xs text-stone-400 mt-4">Cancel any time. No long-term commitment.</p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0D3D4A] text-white py-16 px-6 text-center">
        <h2
          className="text-3xl font-medium mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Ready to transform your referral workflow?
        </h2>
        <p className="text-white/60 text-sm mb-8">Join physicians using Meridian to help patients access affordable care.</p>
        <button
          onClick={() => navigate('/dpc/register')}
          className="bg-white text-[#0D3D4A] px-8 py-4 font-semibold text-sm hover:bg-[#F0EEE9] transition-colors"
        >
          Start Free 14-Day Trial
        </button>
      </section>

    </div>
  );
}
